import { Mesh, StaticSound, Vector3 } from '@babylonjs/core';
import { AssetObject } from '../AssetObject';
import { Level } from '../Level';
import { AssetObjectConfig } from '../MeshFactory';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { PlayerCollision } from '../PlayerCollision';
import { AbstractMeshBehavior } from './AbstractMeshBehavior';
import { ObstacleBehavior } from './ObstacleBehavior';

interface Projectile {
  mesh: Mesh;
  velocity: Vector3;
  lifeTime: number;
}

export class ObstacleCannonBehavior extends ObstacleBehavior {
  private lastFireInterval: number = 0;
  private projectiles: Projectile[] = [];

  // Config defaults
  private fireInterval: number = 2;
  private projectileSpeed: number = 5;
  private projectileLifeTime: number = 20;

  private projectilehitSound: StaticSound;

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleCannon', startPosition, pathIndex, config);
    this.fireInterval = config.fireInterval ?? 2;
    this.projectileSpeed = config.speed ?? 5;
    this.projectileLifeTime = config.fireActiveTime ?? 10;
  }

  update(deltaTime: number): void {
    if (!this.level || !this.level.player) return;

    // 1. Look at player
    const playerPos = this.level.player.getPosition();
    // Keep lookAt horizontal? Or full 3D? Cannon usually rotates Y.
    // Let's do full lookAt if it's a turret, but usually obstacles behave better if Y-locked.
    // However, for a cannon shooting at player, 3D tracking might be cool.
    // Let's stick to horizontal rotation for the base mesh, assuming the mesh is "forward" oriented
    this.lookAt(playerPos);

    // 2. Fire logic
    const currentInterval = Math.floor(this.level.absoluteTime / this.fireInterval);
    if (currentInterval > this.lastFireInterval) {
      this.lastFireInterval = currentInterval;
      if (this.getPlayerDistance() < 50) {
        // Only fire if close
        this.fire();
      }
    }

    // 3. Update projectiles
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.lifeTime -= deltaTime;

      if (p.lifeTime <= 0) {
        this.removeProjectile(i);
        continue;
      }

      // Move
      p.mesh.position.addInPlace(p.velocity.scale(deltaTime));
      p.mesh.rotation.x += 10 * deltaTime;
    }
  }

  private fire(): void {
    if (!this.level) return;

    // Source position: ideally the "gun barrel".
    // We'll approximate from mesh center + slight forward offset
    const forward = this.mesh.forward;
    const spawnPos = this.mesh.position.add(forward.scale(1.5));
    spawnPos.y += 1; // Lift up a bit

    // Create projectile mesh
    const bombAsset = new AssetObject('obstacleProjectile').withClone(true);
    const config = AssetObjectConfig.builder().object(bombAsset).scale(2).physicsShapeType(undefined).build();
    const projectileMesh = config.buildMesh(this.level) as Mesh;
    projectileMesh.position.copyFrom(spawnPos);

    // Direction: towards player (predictive? no, just direct)
    // Or just "forward" of the cannon
    const direction = forward.clone().normalize();
    const p = {
      mesh: projectileMesh,
      velocity: direction.scale(this.projectileSpeed),
      lifeTime: this.projectileLifeTime,
    };
    // Setup player collision for the bomb
    projectileMesh.metadata = {
      ...projectileMesh.metadata,
      playerCollision: new PlayerCollision(false, (level, player, event) => {
        if (!player.vulnerable || level.game.config.godMode || level.finished) {
          // Player is invincible, just destroy bomb
          this.removeProjectile(this.projectiles.indexOf(p));
        } else {
          player.collide(event, 'Hit by bomb!');
          this.removeProjectile(this.projectiles.indexOf(p));
        }
      }),
    };

    // Register collision mesh
    this.level.player?.registerCollisionMesh(projectileMesh);

    // Play sound
    this.level.game.sound.play('explosion', this.mesh, undefined, true);

    this.projectiles.push(p);
  }

  private removeProjectile(index: number): void {
    const p = this.projectiles[index];

    // Create particle effect
    this.level.particles.createDisappearanceParticleSystem(p.mesh, 0.4);

    // Play sound
    this.level.game.sound.play('enemyprojectilehit', this.mesh);

    // Unregister collision
    this.level.player?.unregisterCollisionMesh(p.mesh);

    p.mesh.dispose();
    this.projectiles.splice(index, 1);
  }

  override lookAt(target: Vector3): void {
    // Custom lookAt override if needed, or use base.
    // Base ObstacleBehavior.lookAt does Y-only rotation.
    // Let's use that for now.
    super.lookAt(target);
  }

  override detach(): void {
    // Cleanup projectiles
    for (const p of this.projectiles) {
      p.mesh.dispose();
    }
    this.projectiles = [];
    super.detach();
  }

  initLevel(level: Level): AbstractMeshBehavior {
    super.initLevel(level);
    this.level.game.sound.spatial('enemyprojectilehit', this.mesh, 0, 60);
    return this;
  }

  clone(): ObstacleCannonBehavior {
    return new ObstacleCannonBehavior(this.startPosition, this.pathIndex, this.config);
  }
}
